import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch } from "react-redux";
import {
  addUnlistedInventory,
  assignUnlistedInventory,
  getUnlistedInventory,
  updateUnlistedInventory,
  uploadUnlistedInventory,
} from "../../redux/admin/adminSlice";
import { formatDate } from "../../utils";
import { Close, Search, FileDownload, UploadFile } from "@mui/icons-material";
import { FaEdit } from "react-icons/fa";
import toast from "react-hot-toast";
import userService from "../../redux/user/userService";
import * as XLSX from "xlsx";
import { getRMUser } from "../../redux/user/userSlice";

const UnlistedInventory = () => {
  const dispatch = useDispatch<any>();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [inventory, setInventory] = useState<any[]>([]);
  const [showAddStockModal, setShowAddStockModal] = useState<boolean>(false);
  const [assignModalOpen, setAssignModalOpen] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<number>(3);
  const [formData, setFormData] = useState<any>({
    stockName: "",
    isin: "",
    availableQuantity: "",
    price: "",
  });

  const [assignData, setAssignData] = useState<any>({
    rmId: "",
    quantity: "",
  });

  const [currentData, setCurrentData] = useState<any>(null);
  // CSV upload modal state
  const [csvModalOpen, setCsvModalOpen] = useState<boolean>(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [rms, setRms] = useState<any[]>([]);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    fetchRms();
  }, []);

  useEffect(() => {
    fetchInvenotory();
  }, [searchQuery, pageSize, currentPage, sortBy]);

  const fetchRms = async () => {
    try {
      const response = await dispatch(
        getRMUser({
          userType: 2,
          pageIndex: 1,
          pageSize: 1000,
        })
      );
      const data = response.payload.data;
      setRms(data);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const resetState = () => {
    setFormData({
      stockName: "",
      availableQuantity: "",
      price: "",
    });
    setCurrentData(null);
    setIsEdit(false);
  };

  const fetchInvenotory = async () => {
    const response = await dispatch(
      getUnlistedInventory({
        searchQuery,
        pageSize,
        pageIndex: currentPage,
        sortBy,
      })
    );
    if (response?.error?.message) {
      console.error(
        "Error fetching unlisted inventory:",
        response.error.message
      );
      return;
    }
    setInventory(response.payload.data?.data || []);
    setTotalPages(response.payload.data?.totalPages || 0);
  };

  const handleAddStock = async () => {
    try {
      const response = await dispatch(
        addUnlistedInventory({
          ...formData,
        })
      );
      if (response?.error?.message) {
        toast.error("Error adding stock.");
        return;
      }

      toast.success("Stock added successfully.");
      fetchInvenotory();
      handleClose();
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleEditInventory = async () => {
    try {
      const response = await dispatch(
        updateUnlistedInventory({
          inventoryId: formData?.id,
          stockName: formData?.stockName,
          isin: formData?.isin,
          availableQuantity: formData?.availableQuantity,
          price: formData?.price,
        })
      );
      if (response?.error?.message) {
        toast.error("Error adding stock.");
        return;
      }

      toast.success("Stock updated successfully.");
      fetchInvenotory();
      handleClose();
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleAssignToRM = async () => {
    try {
      const response = await dispatch(
        assignUnlistedInventory({
          ...assignData,
          inventoryId: currentData?.id,
        })
      );
      if (response?.error?.message) {
        toast.error("Error assigning stock to RM.");
        return;
      }

      toast.success("Stock assigned to RM successfully.");
      setAssignModalOpen(false);
      fetchInvenotory();
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleEdit = (data: any) => {
    setFormData(data);
    setShowAddStockModal(true);
    setIsEdit(true);
  };

  const onPageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setCurrentPage(newPage + 1);
  };

  const onPageSizeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleClose = () => {
    setShowAddStockModal(false);
    resetState();
  };

  // Handle upload inventory from Excel
  const handleUploadCsv = async () => {
    if (!csvFile) return;
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      const response = await dispatch(uploadUnlistedInventory(formData));
      if (response?.error?.message) {
        toast.error("Failed to upload inventory.");
        return;
      }
      toast.success("Inventory items uploaded successfully.");
      fetchInvenotory();
      setCsvModalOpen(false);
      setCsvFile(null);
    } catch (error) {
      console.error("Error uploading inventory:", error);
      toast.error("Failed to upload inventory.");
    }
  };

  // Handle file selection
  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setCsvFile(file);
  };
  
  // Export inventory data to Excel
  const exportToExcel = () => {
    if (!inventory || inventory.length === 0) {
      toast.error("No inventory data available to export.");
      return;
    }
    const exportData = inventory.map((stock: any) => ({
      "Stock Name": stock.stockName,
      ISIN: stock.isin || "-",
      "Available Quantity": stock.availableQuantity,
      Price: stock.price,
      "Updated At": formatDate(stock.updatedAt),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Unlisted Inventory");

    const filename = `unlisted_inventory_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div
      className="min-h-screen bg-lightBg py-8 px-4 sm:px-6 lg:px-8"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginBottom: "20px",
      }}
    >
      <Box
        width="90%"
        mb={2}
        sx={{ display: "flex", justifyContent: "space-between" }}
      >
        <Typography variant="h5" style={{ textAlign: "left" }}>
          Unlisted Inventory
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            onClick={() => setCsvModalOpen(true)}
          >
            Upload CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={exportToExcel}
          >
            Export Data
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowAddStockModal(true)}
          >
            Add Inventory
          </Button>
        </Box>
      </Box>
      <Box
        width="90%"
        mb={2}
        sx={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search by stock name or ISIN or Date"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, maxWidth: "400px" }}
        />
        <FormControl sx={{ minWidth: "200px" }}>
          <InputLabel id="sort-label">Sort By</InputLabel>
          <Select
            labelId="sort-label"
            value={sortBy}
            onChange={(e) => setSortBy(Number(e.target.value))}
            label="Sort By"
          >
            <MenuItem value={3}>Quantity (Descending)</MenuItem>
            <MenuItem value={4}>Quantity (Ascending)</MenuItem>
            <MenuItem value={1}>Updated (Descending)</MenuItem>
            <MenuItem value={2}>Updated (Ascending)</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <div style={{ display: "flex", justifyContent: "center", width: "90%" }}>
        <TableContainer
          component={Paper}
          style={{ width: "100%", marginTop: "2rem" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: "bold", minWidth: 120 }}>
                  Name
                </TableCell>
                <TableCell style={{ fontWeight: "bold", minWidth: 120 }}>
                  ISIN
                </TableCell>
                <TableCell style={{ fontWeight: "bold", minWidth: 120 }}>
                  Available Quantity
                </TableCell>
                <TableCell style={{ fontWeight: "bold", minWidth: 120 }}>
                  Price
                </TableCell>
                <TableCell style={{ fontWeight: "bold", minWidth: 120 }}>
                  Created At
                </TableCell>
                <TableCell style={{ fontWeight: "bold", minWidth: 120 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventory &&
                inventory?.map((stock, index) => (
                  <TableRow key={index}>
                    <TableCell>{stock?.stockName}</TableCell>
                    <TableCell>{stock?.isin || "-"}</TableCell>
                    <TableCell>{stock?.availableQuantity}</TableCell>
                    <TableCell>{stock?.price}</TableCell>
                    <TableCell>{formatDate(stock?.updatedAt)}</TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(stock)}
                      >
                        <FaEdit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  count={totalPages * pageSize} // Total number of rows
                  rowsPerPage={pageSize}
                  page={currentPage - 1} // Page is zero-indexed
                  onPageChange={onPageChange}
                  onRowsPerPageChange={onPageSizeChange}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${currentPage} – ${totalPages} `
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>
      <Modal open={showAddStockModal} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 800, // Adjust width as needed
            maxHeight: "80vh", // Limit the height of the modal
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            overflowY: "auto", // Make the content scrollable on the Y-axis
            borderRadius: 2,
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6" component="h2">
              {isEdit ? "Edit Unlisted Stock" : "Add Unlisted Stock"}
            </Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
          <Box component="form" noValidate autoComplete="off">
            <div className="grid grid-cols-2 gap-4">
              <TextField
                label="Stock Name"
                variant="outlined"
                fullWidth
                margin="normal"
                name="stockName"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, stockName: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.stockName || ""}
              />
              <TextField
                label="ISIN"
                variant="outlined"
                fullWidth
                margin="normal"
                name="availableQuantity"
                type="text"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    isin: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.isin || ""}
              />
              <TextField
                label="Total Quantity"
                variant="outlined"
                fullWidth
                margin="normal"
                name="availableQuantity"
                type="text"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    availableQuantity: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.availableQuantity || ""}
              />
              <TextField
                label="Current Price"
                variant="outlined"
                fullWidth
                margin="normal"
                name="price"
                type="number"
                onChange={(e) =>
                  setFormData({ ...formData, price: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.price || ""}
              />
            </div>
            <div className="mt-4">
              <Button
                onClick={() =>
                  isEdit ? handleEditInventory() : handleAddStock()
                }
                color="primary"
                variant="contained"
                sx={{
                  mr: 1,
                }}
                disabled={
                  !formData.stockName ||
                  !formData.availableQuantity ||
                  !formData.price
                }
              >
                Submit
              </Button>
            </div>
          </Box>
        </Box>
      </Modal>
      {/* CSV Upload Modal */}
      <Modal open={csvModalOpen} onClose={() => setCsvModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6" component="h2">
              Upload Inventory CSV
            </Typography>
            <IconButton onClick={() => setCsvModalOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <input
            type="file"
            accept=".csv"
            onChange={handleCsvFileChange}
            style={{ marginBottom: "1rem" }}
          />
          {csvFile && (
            <Typography variant="body2" gutterBottom>
              Selected File: {csvFile.name}
            </Typography>
          )}
          <Box display="flex" gap={2} mt={2}>
            <Button
              variant="contained"
              color="primary"
              disabled={!csvFile}
              onClick={handleUploadCsv}
            >
              Upload
            </Button>
            <Button variant="outlined" onClick={() => setCsvModalOpen(false)}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
      {/* Assign to RM Modal */}
      <Modal open={assignModalOpen} onClose={() => setAssignModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6" component="h2">
              Assign to RM - {currentData?.stockName}
            </Typography>
            <IconButton onClick={() => setAssignModalOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <Box component="form" noValidate autoComplete="off">
            <FormControl fullWidth variant="outlined" margin="normal">
              <InputLabel id="rm-label">Select RM</InputLabel>
              <Select
                labelId="rm-label"
                onChange={(e) =>
                  setAssignData({ ...formData, rmId: e.target.value })
                }
                label="Select RM"
                value={assignData?.rmId || ""}
              >
                {rms.map((rm) => (
                  <MenuItem key={rm.id} value={rm.id}>
                    {rm.firstName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Quantity to Assign"
              variant="outlined"
              fullWidth
              margin="normal"
              name="quantity"
              type="text"
              onChange={(e) =>
                setAssignData({
                  ...assignData,
                  quantity: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
              value={assignData.quantity || ""}
            />
            <div className="mt-4">
              <Button
                onClick={handleAssignToRM}
                color="primary"
                variant="contained"
                sx={{
                  mr: 1,
                }}
                disabled={!assignData.rmId || !assignData.quantity}
              >
                Assign
              </Button>
            </div>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default UnlistedInventory;
